import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon } from '@heroicons/react/24/outline';

import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import SetPasswordDialog from './SetPasswordDialog';

const ConnectedAccountsForm = () => {
  const { user, googleUnlinkAccount } = useAuth();
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);

  const hasPassword = user?.authProvider === 'email' || user?.authProvider === 'both';

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const response = await googleUnlinkAccount();
      return response;
    },
    onSuccess: data => {
      if (data.success) {
        toast({
          title: 'Google account disconnected successfully',
          type: 'success',
        });
        setShowDisconnectModal(false);
      } else {
        throw new Error(data.error);
      }
    },
    onError: error => {
      toast({
        title: error.message || 'Failed to disconnect Google account',
        type: 'error',
      });
    },
  });

  const handleDisconnect = () => {
    if (!hasPassword) {
      setShowSetPasswordDialog(true);
      return;
    }
    setShowDisconnectModal(true);
  };

  const handleSetPasswordSuccess = () => {
    setShowSetPasswordDialog(false);
    // The hasPassword status will be updated automatically since the user now has a password
    // and the authProvider will include 'email' or become 'both'
  };

  // This component should only render when hasGoogleAccount is true
  // The parent component (AccountProfile) handles the conditional rendering

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-500" />
          <div className="text-sm">
            <div className="font-medium text-gray-900 dark:text-foreground">
              Google account connected
            </div>
            <div className="text-gray-600 dark:text-muted-foreground">
              {user?.email && `Connected to: ${user.email}`}
            </div>
          </div>
        </div>
        <Button
          appearance="secondary"
          size="small"
          onClick={handleDisconnect}
          disabled={!hasPassword}
        >
          Disconnect
        </Button>
      </div>

      {!hasPassword && (
        <Alert className="mt-3">
          <AlertDescription>
            <div>
              Set a password before disconnecting your Google account to ensure you can still log
              in.
            </div>
            <Button
              appearance="secondary"
              size="small"
              onClick={() => setShowSetPasswordDialog(true)}
              className="mt-2"
            >
              Set password
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Dialog
        title="Disconnect Google account"
        open={showDisconnectModal}
        onCancel={() => setShowDisconnectModal(false)}
        cancelButton={{
          label: 'Cancel',
          appearance: 'light',
        }}
        confirmButton={{
          label: 'Disconnect account',
          appearance: 'primary',
          onClick: () => disconnectMutation.mutateAsync(),
          loading: disconnectMutation.isLoading,
        }}
      >
        <div className="text-gray-800 dark:text-foreground">
          <p className="mb-3">Are you sure you want to disconnect your Google account?</p>
          <p className="mt-3 text-sm text-gray-600 dark:text-muted-foreground">
            You can always reconnect your Google account later by using "Sign in with Google" on the
            login page.
          </p>
        </div>
      </Dialog>

      <SetPasswordDialog
        open={showSetPasswordDialog}
        onCancel={() => setShowSetPasswordDialog(false)}
        onSuccess={handleSetPasswordSuccess}
      />
    </>
  );
};

export default ConnectedAccountsForm;
